<script setup lang="ts">
import { Swiper } from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css/bundle';
import 'swiper/css/pagination';
import type SiteTopBanner from '@store/content/entities/SiteTopBanner';

const { items } = defineProps<{
    items: SiteTopBanner[];
}>();
const contentStore = useContentStore();
const { homepageMessagesLoading } = classToRefs(contentStore);
const wrapper = ref<HTMLDivElement>();
const pagination = ref<HTMLDivElement>();
const swiper = ref<Swiper>();
onMounted(() => {
    Swiper.use([Pagination, Autoplay]);
    if (wrapper.value) {
        swiper.value = new Swiper(wrapper.value, {
            autoplay: false,
            spaceBetween: 0,
            slidesPerView: 1,
            freeMode: false,
            pagination: {
                el: pagination.value,
                clickable: true,
            },
            breakpoints: {
                768: {
                    spaceBetween: 10,
                    slidesPerView: 'auto',
                    freeMode: true,
                },
            },
        });
        setTimeout(() => swiper.value?.autoplay.start(), 10000);
    }
});

function openUrl(item: SiteTopBanner) {
    if (item?.link) {
        window.open(item.link, '_blank');
    }
}
</script>

<template>
    <div
        ref="wrapper"
        class="banner-carousel-container"
        :class="{ loading: homepageMessagesLoading }"
    >
        <div class="swiper-wrapper">
            <div
                v-for="(item, index) in items"
                :id="`slider-click-${item.id}`"
                :key="item.id ?? `banner-${index}`"
                class="swiper-slide"
                :class="{
                    'loading-skeleton': homepageMessagesLoading,
                    clickable: item.link,
                }"
                :aria-label="
                    item.desktopImage?.alt ??
                    item.mobileImage?.alt ??
                    'لینک بنر'
                "
                data-swiper-autoplay="3000"
                @click="openUrl(item)"
            >
                <mr-blur-hash-image
                    v-if="item.desktopImage"
                    class="desktop-only"
                    :src="item.desktopImage.src"
                    :hash="item.desktopImage.blurHash"
                    :width="592"
                    :height="270"
                    hide-blurhash-onload
                    :alt="item.desktopImage.alt ?? 'بنر سایت مِستربلیط'"
                />
                <mr-blur-hash-image
                    v-if="item.mobileImage"
                    class="mobile-only"
                    :src="item.mobileImage.src"
                    hash="|lENV-EExaf3NgR;oHR.WrcIMxbJs+s*WBs+WEs,E2xWW=WCoea~j@WXoLsRjss+R.WYoLbIaxWEkXNgoIjYn#oIj]f5jY$ws7ofR.W?WrWBbIbINMS6WYoHoIj@jsayodj[ocWCj]azayj[ayayoHoIj@a$WYbHayj[WX"
                    hide-blurhash-onload
                    :alt="item.mobileImage.alt ?? 'بنر سایت مِستربلیط'"
                    :blurhash-config="{ ratio: 2.222222222, size: 125 }"
                />
            </div>
        </div>
        <div ref="pagination" class="swiper-pagination" />
    </div>
</template>

<style lang="scss" scoped>
.clickable {
    cursor: pointer;
}
.banner-carousel-container {
    position: relative;
    width: 100%;
    overflow-x: hidden;
    &.loading {
        overflow-y: hidden;
    }
}

.swiper-slide {
    width: 595px;
    height: 275px;
    > * {
        width: 592px;
        height: 270px;
    }
    &.loading-skeleton {
        pointer-events: none;
    }
    :deep(img) {
        object-fit: cover;
        border-radius: 12px;
    }
    :deep(canvas) {
        border-radius: 12px;
    }
}

.swiper-pagination {
    position: relative !important;
    bottom: 0 !important;
}

@media (max-width: $app-mobile-max-width) {
    .swiper-slide {
        position: relative;
        width: 100%;
        height: auto;
        padding-top: 54.87%;
        > * {
            position: absolute !important;
            top: 0 !important;
            width: 100% !important;
            height: 100% !important;
        }
        :deep(img) {
            object-fit: cover;
            border-radius: 12px;
        }
    }
}
</style>
